import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Trash2 } from "lucide-react";
import { api, apiError, inr2 } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const nextUid = () => `line-${Math.random().toString(36).slice(2, 10)}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const BLANK_LINE = { name: "", quantity: 1, unit: "Nos", rate: 0, discount_percent: 0, gst_rate: 18, hsn_sac: "9506" };

export default function InvoiceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    try {
      const [inv, cust] = await Promise.all([
        api.get(`/invoices/${id}`),
        api.get("/customers?limit=200"),
      ]);
      setInvoice(inv.data);
      setCustomers(cust.data.items);
      setForm({
        customer_id: inv.data.customer_id || "",
        kind: inv.data.kind || "tax_invoice",
        gst_mode: inv.data.gst_mode || "auto",
        project_name: inv.data.project_name || "",
        date: (inv.data.date || "").slice(0, 10),
        due_date: (inv.data.due_date || "").slice(0, 10),
        installation_charge: inv.data.installation_charge || 0,
        transportation_charge: inv.data.transportation_charge || 0,
        other_charges: inv.data.other_charges || 0,
        charges_gst_rate: inv.data.charges_gst_rate ?? 18,
        notes: inv.data.notes || "",
        terms: inv.data.terms || "",
        items: (inv.data.items || []).map((i) => ({ ...i, uid: nextUid() })),
      });
    } catch (e) {
      toast.error(apiError(e));
      navigate("/invoices");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (!form || !invoice) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const setItem = (idx, patch) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));

  let taxable = 0;
  let tax = 0;
  form.items.forEach((it) => {
    const gross = round2(Number(it.quantity) * Number(it.rate));
    const line = round2(gross - (gross * Number(it.discount_percent || 0)) / 100);
    taxable += line;
    tax += round2((line * Number(it.gst_rate || 0)) / 100);
  });
  const charges = round2(
    Number(form.installation_charge || 0) + Number(form.transportation_charge || 0) +
      Number(form.other_charges || 0)
  );
  taxable = round2(taxable + charges);
  tax = round2(tax + (charges * Number(form.charges_gst_rate || 0)) / 100);
  const grand = round2(taxable + tax);
  const paid = Number(invoice.paid_amount || 0);

  const save = async () => {
    const items = form.items
      .filter((i) => (i.name || "").trim())
      .map(({ uid, ...i }) => ({
        ...i, quantity: Number(i.quantity || 0), rate: Number(i.rate || 0),
        discount_percent: Number(i.discount_percent || 0), gst_rate: Number(i.gst_rate || 0),
      }));
    if (!items.length) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const { data } = await api.put(`/invoices/${id}`, {
        ...form, items,
        installation_charge: Number(form.installation_charge || 0),
        transportation_charge: Number(form.transportation_charge || 0),
        other_charges: Number(form.other_charges || 0),
        charges_gst_rate: Number(form.charges_gst_rate || 0),
      });
      toast.success(`Invoice ${data.number} updated`);
      navigate("/invoices");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="invoice-editor">
      <PageHeader
        title={`Edit ${invoice.number}`}
        subtitle="Change line items, charges and terms. Saving recalculates GST and refreshes the PDF."
        actions={
          <>
            <Button variant="outline" data-testid="invoice-cancel-edit-button" onClick={() => navigate("/invoices")}>
              Cancel
            </Button>
            <Button data-testid="invoice-update-button" disabled={saving} onClick={save}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
            </Button>
          </>
        }
      />

      <div className="grid gap-4 p-4 sm:p-6 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          <div className="surface p-5">
            <div className="overline mb-3">Invoice details</div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Customer">
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="invoice-edit-customer-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Invoice type">
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="invoice-edit-kind-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[["tax_invoice", "Tax Invoice"], ["proforma", "Proforma Invoice"],
                      ["advance", "Advance Invoice"], ["credit_note", "Credit Note"],
                      ["debit_note", "Debit Note"]].map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="GST type">
                <Select value={form.gst_mode} onValueChange={(v) => setForm({ ...form, gst_mode: v })}>
                  <SelectTrigger data-testid="invoice-edit-gst-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automatic (from customer state)</SelectItem>
                    <SelectItem value="intra">CGST + SGST</SelectItem>
                    <SelectItem value="inter">IGST</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Project">
                <Input
                  data-testid="invoice-edit-project-input"
                  value={form.project_name}
                  onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                />
              </Field>
              <Field label="Invoice date">
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </Field>
              <Field label="Due date">
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </Field>
            </div>
          </div>

          <div className="surface p-5">
            <div className="overline mb-3">Line items</div>
            <div className="space-y-3">
              {form.items.map((it, i) => (
                <div key={it.uid} className="rounded border border-border p-3">
                  <div className="mb-2 flex items-start gap-2">
                    <Input
                      data-testid={`invoice-edit-item-name-${i}`}
                      placeholder="Description"
                      value={it.name}
                      onChange={(e) => setItem(i, { name: e.target.value })}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`invoice-edit-item-remove-${i}`}
                      onClick={() => setForm({ ...form, items: form.items.filter((x) => x.uid !== it.uid) })}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
                    {[["quantity", "Qty"], ["unit", "Unit"], ["rate", "Rate ₹"],
                      ["discount_percent", "Disc %"], ["gst_rate", "GST %"], ["hsn_sac", "HSN"]].map(([k, l]) => (
                      <Field key={k} label={l}>
                        <Input
                          data-testid={`invoice-edit-item-${k}-${i}`}
                          value={it[k] ?? ""}
                          onChange={(e) => setItem(i, { [k]: e.target.value })}
                        />
                      </Field>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              data-testid="invoice-edit-add-line-button"
              onClick={() => setForm({ ...form, items: [...form.items, { uid: nextUid(), ...BLANK_LINE }] })}
            >
              Add line
            </Button>
          </div>

          <div className="surface p-5">
            <div className="overline mb-3">Charges, notes & terms</div>
            <div className="grid gap-3 sm:grid-cols-4">
              {[["installation_charge", "Installation ₹"], ["transportation_charge", "Transportation ₹"],
                ["other_charges", "Other charges ₹"], ["charges_gst_rate", "Charges GST %"]].map(([k, l]) => (
                <Field key={k} label={l}>
                  <Input
                    data-testid={`invoice-edit-${k}`}
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  />
                </Field>
              ))}
              <Field label="Notes" className="sm:col-span-2">
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
              <Field label="Terms" className="sm:col-span-2">
                <Textarea value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} />
              </Field>
            </div>
          </div>
        </div>

        <div className="surface h-fit p-5 lg:sticky lg:top-20" data-testid="invoice-edit-totals">
          <div className="overline mb-3">Live totals</div>
          {[["Taxable value", taxable], [form.gst_mode === "inter" ? "IGST" : "CGST + SGST", tax]].map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-border py-2 text-sm">
              <span className="text-muted-foreground">{k}</span>
              <span className="font-mono-num">{inr2(v)}</span>
            </div>
          ))}
          <div className="mt-3 flex items-center justify-between rounded bg-primary px-3 py-2.5 text-primary-foreground">
            <span className="text-xs uppercase tracking-wider">Grand total</span>
            <span className="font-mono-num text-lg font-semibold" data-testid="invoice-edit-grand-total">
              {inr2(grand)}
            </span>
          </div>
          <div className="mt-4 space-y-1 border-t border-border pt-3 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Already received</span>
              <span className="font-mono-num">{inr2(paid)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Balance</span>
              <span className="font-mono-num">{inr2(round2(grand - paid))}</span>
            </div>
          </div>
          {paid > 0 && (
            <Badge variant="outline" className="mt-3 text-[10px]" data-testid="invoice-edit-paid-warning">
              ₹{paid.toLocaleString("en-IN")} already received — total cannot go below this
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}
